import { LazyPathwayDiagram } from '@/components/diagrams/LazyPathwayDiagram';
import { CardActions } from '@/components/editor/CardActions';
import { EditableText } from '@/components/editor/EditableText';
import { useEditorMode } from '@/components/editor/EditorContext';
import { SortableSection } from '@/components/editor/SortableFicheLayer';
import { FICHE_COLOR_HEX } from '@/lib/colors';
import { type LayoutGroup, groupSections } from '@/lib/layout';
import { useEditorStore } from '@/lib/state/editorStore';
import type { Fiche, FicheColor, FicheSection } from '@pharmafiche/schema';
import { Fragment } from 'react';
import { Bullets } from './_shared/Bullets';
import { CardHead } from './_shared/CardHead';
import { MiniHeaderPill } from './_shared/MiniHeader';
import { MnemonicBox } from './_shared/MnemonicBox';
import { SectionTitleBar } from './_shared/SectionTitleBar';
import { Sparkles } from './_shared/Sparkles';
import { WarnBox } from './_shared/WarnBox';

function groupKey(g: LayoutGroup): string {
  if (g.kind === 'full') return `g-${g.section.id}`;
  return `g-${g.sections[0]?.id ?? 'empty'}`;
}

export interface FicheEditHandlers {
  updateMetaTitle?: (title: string) => void;
  updateSectionTitle?: (sectionId: string, title: string) => void;
  updateBulletText?: (sectionId: string, bulletId: string, text: string) => void;
  updateDrugName?: (sectionId: string, name: string) => void;
  updateDrugSubsectionLabel?: (sectionId: string, subId: string, label: string) => void;
  updateDrugSubsectionBulletText?: (
    sectionId: string,
    subId: string,
    bulletId: string,
    text: string,
  ) => void;
  updateMnemonicText?: (sectionId: string, text: string) => void;
  updateWarningText?: (sectionId: string, text: string) => void;
  updateClinicalPatient?: (sectionId: string, text: string) => void;
  updateClinicalQuestion?: (sectionId: string, text: string) => void;
  updateSectionWarning?: (sectionId: string, warning: string) => void;
  moveSection?: (sectionId: string, dir: 'up' | 'down') => void;
  duplicateSection?: (sectionId: string) => void;
  removeSection?: (sectionId: string) => void;
  recolorSection?: (sectionId: string, color: FicheColor) => void;
  reorderSections?: (fromIndex: number, toIndex: number) => void;
}

interface FicheDessineeProps {
  fiche: Fiche;
  editHandlers?: FicheEditHandlers;
}

const PAPER_BG = {
  background: '#fdfaf1',
  backgroundImage:
    'linear-gradient(#e7dec8 1px, transparent 1px), linear-gradient(90deg, #e7dec8 1px, transparent 1px)',
  backgroundSize: '28px 28px',
  backgroundPosition: '-1px -1px',
};

const SHEET_SHADOW = {
  boxShadow:
    '0 1px 0 rgba(0,0,0,.04), 0 12px 28px rgba(60,40,20,.18), 0 30px 60px rgba(60,40,20,.12)',
};

const DESK_BG = {
  background:
    'radial-gradient(circle at 20% 30%, rgba(255,200,140,.18), transparent 40%), radial-gradient(circle at 80% 70%, rgba(140,180,255,.15), transparent 45%), linear-gradient(#ece5d2, #e0d8c0)',
};

interface HeaderProps {
  title: string;
  subtitle?: string | undefined;
  drugFamilies: { color: FicheColor; label: string }[];
  onCommitTitle?: (next: string) => void;
}

function FicheHeader({ title, subtitle, drugFamilies, onCommitTitle }: HeaderProps) {
  return (
    <div className="flex flex-col items-center gap-[6px] mb-[22px] relative">
      <div
        className="bg-accent-yellow border-[2.5px] border-ink rounded-[14px] px-7 pt-[10px] pb-2 font-gloock text-center leading-[1.05] text-ink shadow-ink-lg"
        style={{
          fontSize: 'clamp(26px, 5.4vw, 40px)',
          letterSpacing: '0.04em',
          transform: 'rotate(-1deg)',
        }}
      >
        <EditableText
          value={title}
          plain
          ariaLabel="Titre de la fiche"
          onCommit={(next) => onCommitTitle?.(next)}
        />
      </div>
      {subtitle ? (
        <div
          className="font-caveat text-ink-soft text-center"
          style={{ fontSize: 'clamp(18px, 3.4vw, 22px)' }}
        >
          {subtitle}
        </div>
      ) : null}
      {drugFamilies.length > 0 ? (
        <div
          className="font-caveat text-ink-soft flex flex-wrap justify-center mt-1"
          style={{ fontSize: 'clamp(18px, 3.4vw, 22px)', gap: '6px 14px' }}
        >
          {drugFamilies.map((f) => (
            <span key={`${f.color}-${f.label}`}>
              <span
                className="inline-block rounded-full mr-1"
                style={{
                  width: 8,
                  height: 8,
                  background: FICHE_COLOR_HEX[f.color],
                  transform: 'translateY(-2px)',
                }}
              />
              <b style={{ fontWeight: 600 }}>{f.label}</b>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface CardWrapperProps {
  color: FicheColor;
  lean: 'l' | 'r' | 'none';
  sectionId?: string;
  editHandlers?: FicheEditHandlers;
  children: React.ReactNode;
}

function CardWrapper({ color, lean, sectionId, editHandlers, children }: CardWrapperProps) {
  const mode = useEditorMode();
  const focusedSectionId = useEditorStore((s) => s.focusedSectionId);
  const setFocusedSection = useEditorStore((s) => s.setFocusedSection);
  const transform = lean === 'l' ? 'rotate(-.3deg)' : lean === 'r' ? 'rotate(.3deg)' : undefined;
  const showActions = mode === 'edit' && editHandlers && sectionId;
  const isFocused = !!sectionId && focusedSectionId === sectionId;

  const card = (
    // biome-ignore lint/a11y/useKeyWithClickEvents: focus toggle is a soft AI scope hint, not a primary action
    <div
      className="rounded-[14px] px-[14px] pt-[14px] pb-3 relative group cursor-pointer"
      style={{
        background: 'rgba(255,255,255,.6)',
        border: '2.5px solid #1f2330',
        boxShadow: isFocused
          ? '3px 4px 0 #2e6cb5, 0 0 0 3px rgba(46,108,181,0.35)'
          : '3px 4px 0 #1f2330',
        ...(transform ? { transform } : {}),
        ['--c' as never]: FICHE_COLOR_HEX[color],
      }}
      onClick={(e) => {
        if (!sectionId) return;
        // Don't toggle focus when clicking on an editable text or button inside
        const target = e.target as HTMLElement | null;
        if (
          target?.closest('[contenteditable="true"]') ||
          target?.closest('button') ||
          target?.closest('a')
        ) {
          return;
        }
        setFocusedSection(isFocused ? null : sectionId);
      }}
    >
      {children}
      {showActions ? <CardActions sectionId={sectionId} editHandlers={editHandlers} /> : null}
    </div>
  );

  if (mode === 'edit' && sectionId) {
    return <SortableSection sectionId={sectionId}>{card}</SortableSection>;
  }
  return card;
}

// ─── Section renderers ──────────────────────────────────────────────────────

function IntroLikeCard({
  section,
  num,
  lean,
  editHandlers,
}: {
  section: Extract<FicheSection, { type: 'intro' | 'classification' }>;
  num: number;
  lean: 'l' | 'r' | 'none';
  editHandlers?: FicheEditHandlers;
}) {
  return (
    <CardWrapper
      color={section.color}
      lean={lean}
      sectionId={section.id}
      editHandlers={editHandlers}
    >
      <CardHead
        num={num}
        icon={section.icon}
        label={section.title}
        color={section.color}
        editableTitle
        onTitleCommit={(t) => editHandlers?.updateSectionTitle?.(section.id, t)}
      />
      <Bullets
        bullets={section.bullets}
        accent={section.color}
        onBulletChange={
          editHandlers?.updateBulletText
            ? (id, t) => editHandlers.updateBulletText?.(section.id, id, t)
            : undefined
        }
      />
      {section.type === 'intro' && section.warning ? (
        <WarnBox>
          ⚠️{' '}
          <EditableText
            value={section.warning}
            ariaLabel="Avertissement"
            onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
          />
        </WarnBox>
      ) : null}
      {section.type === 'classification' && section.note ? (
        <WarnBox variant="amber">
          <EditableText
            value={section.note}
            ariaLabel="Note"
            onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
          />
        </WarnBox>
      ) : null}
    </CardWrapper>
  );
}

function DrugCard({
  section,
  num,
  lean,
  editHandlers,
}: {
  section: Extract<FicheSection, { type: 'drug_card' }>;
  num: number;
  lean: 'l' | 'r' | 'none';
  editHandlers?: FicheEditHandlers;
}) {
  const { drug } = section;
  return (
    <CardWrapper color={drug.color} lean={lean} sectionId={section.id} editHandlers={editHandlers}>
      <CardHead
        num={num}
        icon={drug.icon}
        label={drug.name}
        color={drug.color}
        dense
        editableTitle
        onTitleCommit={(t) => editHandlers?.updateDrugName?.(section.id, t)}
      />
      <Bullets
        bullets={drug.intro_bullets}
        accent={drug.color}
        dense
        onBulletChange={
          editHandlers?.updateBulletText
            ? (id, t) => editHandlers.updateBulletText?.(section.id, id, t)
            : undefined
        }
      />
      {drug.sub_sections.map((mh) => (
        <Fragment key={mh.id}>
          <MiniHeaderPill
            icon={mh.icon}
            label={mh.label}
            color={mh.color}
            dense
            onLabelCommit={(t) => editHandlers?.updateDrugSubsectionLabel?.(section.id, mh.id, t)}
          />
          <Bullets
            bullets={mh.bullets}
            accent={mh.color ?? drug.color}
            dense
            onBulletChange={
              editHandlers?.updateDrugSubsectionBulletText
                ? (id, t) => editHandlers.updateDrugSubsectionBulletText?.(section.id, mh.id, id, t)
                : undefined
            }
          />
        </Fragment>
      ))}
    </CardWrapper>
  );
}

function ClinicalCaseCard({
  section,
  num,
  lean,
  editHandlers,
}: {
  section: Extract<FicheSection, { type: 'clinical_case' }>;
  num: number;
  lean: 'l' | 'r' | 'none';
  editHandlers?: FicheEditHandlers;
}) {
  return (
    <CardWrapper color="red" lean={lean} sectionId={section.id} editHandlers={editHandlers}>
      <CardHead num={num} icon="🩺" label="Cas clinique" color="red" />
      <div className="font-hand text-[15.5px] leading-[1.35] text-ink mt-1">
        <EditableText
          value={section.patient}
          ariaLabel="Patient"
          onCommit={(t) => editHandlers?.updateClinicalPatient?.(section.id, t)}
        />
      </div>
      <Bullets
        accent="red"
        bullets={section.prescription.map((p, i) => ({
          id: `${section.id}-rx-${i}`,
          text: p,
        }))}
      />
      <div className="font-hand text-[15.5px] leading-[1.35] text-ink mt-2">
        <b>Question :</b>{' '}
        <EditableText
          value={section.question}
          ariaLabel="Question"
          onCommit={(t) => editHandlers?.updateClinicalQuestion?.(section.id, t)}
        />
      </div>
      <MiniHeaderPill icon="✅" label="Conduite à tenir" />
      <Bullets
        accent="red"
        bullets={section.conduite}
        onBulletChange={
          editHandlers?.updateBulletText
            ? (id, t) => editHandlers.updateBulletText?.(section.id, id, t)
            : undefined
        }
      />
    </CardWrapper>
  );
}

function FreeTextCard({
  section,
  num,
  lean,
  editHandlers,
}: {
  section: Extract<FicheSection, { type: 'free_text' }>;
  num: number;
  lean: 'l' | 'r' | 'none';
  editHandlers?: FicheEditHandlers;
}) {
  return (
    <CardWrapper color="neutral" lean={lean} sectionId={section.id} editHandlers={editHandlers}>
      {section.title ? <CardHead num={num} label={section.title} color="neutral" /> : null}
      <p className="font-hand text-[15.5px] leading-[1.4] text-ink whitespace-pre-wrap">
        {section.body}
      </p>
    </CardWrapper>
  );
}

function leanFor(index: number): 'l' | 'r' | 'none' {
  if (index % 2 === 0) return 'l';
  return 'r';
}

function renderCard(
  section: FicheSection,
  num: number,
  indexInRow: number,
  editHandlers?: FicheEditHandlers,
) {
  const lean = leanFor(indexInRow);
  switch (section.type) {
    case 'intro':
    case 'classification':
      return <IntroLikeCard section={section} num={num} lean={lean} editHandlers={editHandlers} />;
    case 'drug_card':
      return <DrugCard section={section} num={num} lean={lean} editHandlers={editHandlers} />;
    case 'clinical_case':
      return (
        <ClinicalCaseCard section={section} num={num} lean={lean} editHandlers={editHandlers} />
      );
    case 'free_text':
      return <FreeTextCard section={section} num={num} lean={lean} editHandlers={editHandlers} />;
    default:
      return null;
  }
}

// ─── Main template ──────────────────────────────────────────────────────────

export function FicheDessinee({ fiche, editHandlers }: FicheDessineeProps) {
  const groups = groupSections(fiche.sections);

  const sectionNumbers = new Map<string, number>();
  let counter = 0;
  for (const sec of fiche.sections) {
    if (
      sec.type === 'intro' ||
      sec.type === 'classification' ||
      sec.type === 'drug_card' ||
      sec.type === 'clinical_case' ||
      sec.type === 'free_text'
    ) {
      counter += 1;
      sectionNumbers.set(sec.id, counter);
    } else if (sec.type === 'diagram' || sec.type === 'comparison_table') {
      counter += 1;
      sectionNumbers.set(sec.id, counter);
    }
  }

  const drugFamilies: { color: FicheColor; label: string }[] = [];
  for (const sec of fiche.sections) {
    if (sec.type === 'classification') {
      for (const b of sec.bullets) {
        const m = b.text.match(/\*\*([^*]+)\*\*/);
        const label = m?.[1] ?? b.text;
        const color = (b.emphasis ?? sec.color) as FicheColor;
        drugFamilies.push({ color, label: label.split(':')[0]?.trim() ?? label });
      }
      break;
    }
  }

  const diagramsById = new Map(fiche.diagrams.map((d) => [d.id, d]));
  let drugCardTitleEmitted = false;

  function gridCols(cols: number): string {
    const safe = Math.max(2, Math.min(5, cols));
    if (safe === 5) return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-[14px]';
    if (safe === 4) return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[14px]';
    if (safe === 3) return 'grid grid-cols-1 md:grid-cols-3 gap-[14px]';
    return 'grid grid-cols-1 md:grid-cols-2 gap-[14px]';
  }

  return (
    <div style={DESK_BG} className="min-h-screen px-4 py-8">
      <div
        className="relative max-w-[880px] mx-auto rounded-[14px] px-[26px] pt-7 pb-9 overflow-hidden"
        style={{ ...PAPER_BG, ...SHEET_SHADOW }}
      >
        <Sparkles />

        <FicheHeader
          title={fiche.meta.title}
          subtitle={fiche.meta.subtitle}
          drugFamilies={drugFamilies}
          {...(editHandlers?.updateMetaTitle
            ? { onCommitTitle: editHandlers.updateMetaTitle }
            : {})}
        />

        {groups.map((g, gi) => {
          const isFirstDrugRow =
            g.kind === 'row' &&
            g.sections.some((s) => s.type === 'drug_card') &&
            !drugCardTitleEmitted;
          if (isFirstDrugRow) drugCardTitleEmitted = true;

          if (g.kind === 'full') {
            const sec = g.section;
            if (sec.type === 'diagram') {
              const diag = diagramsById.get(sec.diagram_id);
              const titleText = diag?.title
                ? `${sectionNumbers.get(sec.id) ?? ''}. ${diag.title}`.replace(/^\.\s/, '')
                : null;
              return (
                <Fragment key={groupKey(g)}>
                  {titleText ? <SectionTitleBar text={titleText} /> : null}
                  {diag ? (
                    <LazyPathwayDiagram diagram={diag} />
                  ) : (
                    <div className="font-hand text-ink-soft text-center py-8">
                      Diagramme <code>{sec.diagram_id}</code> introuvable
                    </div>
                  )}
                </Fragment>
              );
            }
            if (sec.type === 'mnemonic') {
              return (
                <MnemonicBox
                  key={groupKey(g)}
                  text={sec.text}
                  onTextCommit={
                    editHandlers?.updateMnemonicText
                      ? (t) => editHandlers.updateMnemonicText?.(sec.id, t)
                      : undefined
                  }
                />
              );
            }
            if (sec.type === 'warning') {
              return (
                <WarnBox key={groupKey(g)} final>
                  {sec.icon ?? '⚠️'}{' '}
                  <EditableText
                    value={sec.text}
                    ariaLabel="Avertissement final"
                    onCommit={(t) => editHandlers?.updateWarningText?.(sec.id, t)}
                  />
                </WarnBox>
              );
            }
            return null;
          }

          // row
          return (
            <Fragment key={groupKey(g)}>
              {isFirstDrugRow ? <SectionTitleBar text="Les molécules en détail" /> : null}
              <div className={gridCols(g.cols)} style={{ marginTop: gi === 0 ? 0 : 14 }}>
                {g.sections.map((sec, idx) => (
                  <Fragment key={sec.id}>
                    {renderCard(sec, sectionNumbers.get(sec.id) ?? 0, idx, editHandlers)}
                  </Fragment>
                ))}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
